import { isTauri } from '@tauri-apps/api/core';
import { useAuthStore } from 'cloak-auth/src/Client/stores';
import { useGetDeviceGroups } from 'gen/client';
import { useCallback } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import { getOrCreateRenkiConfig, updateRenkiConfig } from './renkiInvoke';

export type RenkiConfig = z.infer<typeof renkiConfigSchema>;

export type RenkiConfigUpdate = z.infer<typeof renkiConfigUpdateSchema>;

const renkiConfigSchema = z.object({
    defaultLander: z.string().url().optional(),
    discoverIgnore: z.boolean().optional(),
    favoriteInstance: z.string().optional(),
    installationId: z.string().optional(),
});

const renkiConfigUpdateSchema = renkiConfigSchema.omit({
    installationId: true,
});

async function tauriFetchRenkiConfig(): Promise<RenkiConfig> {
    const cloak = useAuthStore.getState().keyCloak;
    if (!cloak) {
        throw new Error('Cloak instance is not available');
    }

    await cloak.updateToken(5);
    const token = cloak.token!;
    const res = await getOrCreateRenkiConfig(token);
    return res;
}

function webFetchRenkiConfig(): RenkiConfig {
    const val = localStorage.getItem('renkiConfig');
    if (!val) {
        return renkiConfigSchema.parse({});
    }

    const parsed = renkiConfigSchema.safeParse(JSON.parse(val));
    if (!parsed.success) {
        return renkiConfigSchema.parse({});
    }

    return parsed.data;
}

function webUpdateRenkiConfig(config: RenkiConfigUpdate): RenkiConfig {
    const parsed = renkiConfigUpdateSchema.safeParse(config);
    if (!parsed.success) {
        console.error('Invalid configuration data', parsed.error);
        throw new Error('Invalid configuration data');
    }

    const oldConfig = webFetchRenkiConfig();
    const newConfig = { ...oldConfig, ...parsed.data };

    localStorage.setItem('renkiConfig', JSON.stringify(newConfig));
    return newConfig;
}

export const useRenkiConfig = () => {
    const tauri = isTauri();
    const {
        data: config,
        isLoading: loading,
        mutate,
    } = useSWR<RenkiConfig>('_/renkiConfig', {
        dedupingInterval: 10_000,
        fetcher: tauri ? tauriFetchRenkiConfig : webFetchRenkiConfig,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const updateConfig = useCallback(
        async (newConfig: RenkiConfigUpdate) => {
            let updatedConfig: RenkiConfig;
            if (tauri) {
                const nullUndefineds = Object.fromEntries(
                    Object.entries(newConfig).map(([key, value]) => [key, (value as typeof value | undefined) ?? null]),
                );
                updatedConfig = await updateRenkiConfig(nullUndefineds);
            } else {
                updatedConfig = webUpdateRenkiConfig(newConfig);
            }
            await mutate(updatedConfig, { revalidate: false });
        },
        [mutate, tauri],
    );

    return { config, loading, updateConfig };
};

export const useIsNewDevice = () => {
    const { config, loading } = useRenkiConfig();
    const { data: deviceGroups } = useGetDeviceGroups();
    if (loading || !deviceGroups) {
        return { isNewDevice: false, loading: true };
    }

    const isNewDevice = !deviceGroups.some((i) => i.installation_id === config?.installationId);

    return { isNewDevice, loading: false };
};
